import { useMemo } from "react";
import { TipDetails } from "model/Tip";
import moment, { Moment } from "moment";
import { formatNumber } from "utils/utils";
import { useTranslation } from "react-i18next";
import { FontSize, FontFamily } from "config/font";
import { buildMonthlyReport } from "./buildMonthlyReport";
import {
  TipCell,
  TipHeadCell,
  TipColumnDivider,
  MonthlyGridHeader,
  MonthlyCellContainer,
} from "../tips.styled";
import { Text } from "components/Typography/text.styled";
import { ArrowSign } from "web-apps/company/components/salary/salary.styled";
import { MonthSelectContainer } from "web-apps/company/components/salary/salary.styled";

interface MonthlyReportProps {
  tips: TipDetails[];
  currentDate: Moment;
  setCurrentDate: (date: Moment) => void;
}

export const MonthlyReport: React.FC<MonthlyReportProps> = (props) => {
  const { t } = useTranslation();

  const rows = useMemo(() => buildMonthlyReport(props.tips), [props.tips]);

  const handlePreviousMonth = () => {
    props.setCurrentDate(moment(props.currentDate).subtract(1, "months"));
  };

  const handleNextMonth = () => {
    props.setCurrentDate(moment(props.currentDate).add(1, "months"));
  };

  const totalForMonth = useMemo(() => {
    return rows.reduce((acc, r) => acc + r.swishAmount + r.manualAmount, 0);
  }, [rows]);

  const capitalizedMonth =
    props.currentDate.format("MMMM")[0].toUpperCase() +
    props.currentDate.format("MMMM").slice(1);

  const formatDecimalHours = (decimalHours: number): string => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return minutes === 0 ? `${hours}t` : `${hours}t ${minutes}m`;
  };

  return (
    <>
      <MonthSelectContainer>
        <ArrowSign
          onClick={handlePreviousMonth}
          title={t("CompanyTip.PreviousMonth")}
        >
          &#60;
        </ArrowSign>
        <Text
          fontSize={FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
          style={{ margin: 0 }}
        >
          {capitalizedMonth} {props.currentDate.year()}
        </Text>

        <ArrowSign onClick={handleNextMonth} title={t("CompanyTip.NextMonth")}>
          &#62;
        </ArrowSign>
      </MonthSelectContainer>

      <MonthlyGridHeader>
        <TipHeadCell style={{ display: "flex", justifyContent: "start" }}>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.Name")}
          </Text>
        </TipHeadCell>
        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.TotalTip")}
          </Text>
        </TipHeadCell>{" "}
        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.Hours")}
          </Text>
        </TipHeadCell>
        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.Swish")}
          </Text>
        </TipHeadCell>
        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.Manually")}
          </Text>
        </TipHeadCell>{" "}
      </MonthlyGridHeader>
      {rows.length === 0 ? (
        <Text>{t("MonthlyTipReport.NoTimeReportsToShow")}</Text>
      ) : (
        <>
          {rows.map((r) => (
            <MonthlyCellContainer key={r.workerId}>
              <TipCell style={{ display: "flex", justifyContent: "start" }}>
                <Text>{r.name}</Text>
              </TipCell>{" "}
              <TipColumnDivider />
              <TipCell>
                {" "}
                <Text>
                  {formatNumber(r.swishAmount + r.manualAmount)}{" "}
                  {t("CompanyTip.Kr")}
                </Text>
              </TipCell>{" "}
              <TipColumnDivider />
              <TipCell>
                {" "}
                <Text>{formatDecimalHours(r.totalHours)}</Text>
              </TipCell>
              <TipColumnDivider />
              <TipCell>
                {" "}
                <Text>
                  {" "}
                  {formatNumber(r.swishAmount)} {t("CompanyTip.Kr")}
                </Text>
              </TipCell>
              <TipColumnDivider />{" "}
              <TipCell>
                {" "}
                <Text>
                  {" "}
                  {formatNumber(r.manualAmount)} {t("CompanyTip.Kr")}
                </Text>
              </TipCell>
            </MonthlyCellContainer>
          ))}
          <Text
            style={{ display: "flex", justifyContent: "flex-end" }}
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("MonthlyTipReport.TotalPayout")}&nbsp;
            {formatNumber(totalForMonth)}&nbsp;{t("CompanyTip.Kr")}
          </Text>
        </>
      )}
    </>
  );
};
